import { fetchCustTotalProductSummary } from '../../infra/api/fetchCustTotalProductSummary';
import { useBaseParam } from '../hooks/useBaseParam';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as Atom from '../recoil/index';
import { useEffect, useState } from 'react';
import useNav from '../hooks/useNav';
import { useParams } from 'react-router-dom';
import { useBridge } from '../hooks/useBridge';
import { currencyKR } from '../helper/filter';
import moment from 'moment/moment';
import { beaconSelector } from '../recoil/selector';
import useBeacon from '../hooks/useBeacon';
import { PAGE_NM, PLACEMENT_TYPE } from '../types/types';
import { useBanner } from '../hooks/useBanner';
import useRedirect from '../hooks/useRedirect';

const useResultEntry = () => {
  const { isRedirect } = useRedirect();

  const { navigate } = useNav();
  const { media } = useParams();
  const br: any = useBridge();
  const beaconData = useRecoilValue(beaconSelector);
  const [baseContents] = useRecoilState(Atom.baseContents);

  const beacon = useBeacon({
    mediaCd: beaconData.mediaCd,
    custSeq: beaconData.custSeq,
    srcCd: beaconData.srcCd,
    pageName: PAGE_NM.RESULT,
  });
  useEffect(() => {
    beacon.sendViewPage();
  }, []);

  const sendBeaconClkBtnDetailList = (btnNm: string) => {
    beacon.sendClkBtnDetailList({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnRateRecord = (btnNm: string) => {
    beacon.sendClkBtnRateRecord({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnLimitRecord = (btnNm: string) => {
    beacon.sendClkBtnLimitRecord({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnNotiSchAlrt = (btnNm: string) => {
    beacon.sendClkBtnNotiSchAlrt({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnIntrstCalc = (btnNm: string) => {
    beacon.sendClkBtnIntrstCalc({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnLoanPrdRecentHit = (btnNm: string) => {
    beacon.sendClkBtnLoanPrdRecentHit({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBtnEbmpOutlink = (btnNm: string) => {
    beacon.sendClkBtnEbmpOutlink({
      properties: { btnNm },
    });
  };
  const sendBeaconClkBnrBnrPromotion = (btnNm: string) => {
    beacon.sendClkBnrBnrPromotion({
      properties: {
        btnNm,
        promotion_type: 'is',
        promotion_str_dt: '2023-04-17',
        promotion_end_dt: '2023-05-31',
        promotion_seq: '2',
      },
    });
  };

  const navigateBannerToPage = (idx: number) => {
    const banner = ['/notiPreReg', '/rateCalc'];
    navigate(banner[idx]);
  };

  const [personal, setPersonal] = useRecoilState(Atom.personalInfo);
  const [todayLoanInfo, setTodayLoanInfo] = useState<any>(undefined);
  const [isBridgeModal, setIsBridgeModal] = useState<boolean>(false);
  const [sort, setSort] = useState<string>('rate');
  const [height] = useState<any>([]);
  const sortingItems = [
    { label: '금리', value: 'rate' },
    { label: '한도', value: 'amount' },
  ];

  const changeSort = (i: number) => {
    setSort(sortingItems[i].value);
  };

  useEffect(() => {
    if (personal?.custSeq) {
      void getCustProductInfoDay();
    }
  }, []);

  const closeWebview = () => {
    br?.close();
  };

  const navigateNativeMain = () => {
    br?.goNativeMain();
  };

  const navigateDA = (media?: string) => {
    setIsBridgeModal(false);
    if (media === 'skt') {
      const trackCdType = 'A007BA01'; // A007BA01
      br?.requestAddService({
        type: 'EBMP',
        url:
          process.env.REACT_APP_EBMP_SKT_URI +
          `?extraParam=3&extraParam2=${trackCdType}`,
      });
    } else if (media === 'kt') {
      const srcCd = 'D007BA01'; // D007BA01
      br?.requestAddService({
        type: 'EBMP',
        url: process.env.REACT_APP_EBMP_KT_URI + `?src_cd=${srcCd}`,
      });
    } else {
      const srcCd = 'B007BA01'; // B007BC02
      const redirectYn = 'Y';
      br?.requestAddService({
        type: 'EBMP',
        url: `?tabSeq=1&track_cd_type=${srcCd}&redirectYn=${redirectYn}`,
      });
    }
  };

  const onClickNaviButton = () => {
    setIsBridgeModal(true);
  };

  const [showAlert, setShowAlert] = useState<boolean>(false);
  const useMtgLoan = useRecoilValue(Atom.useMtgLoan);
  const closeCreditLoan = () => {
    if (useMtgLoan) navigate('/main', { replace: true });
    else closeWebview();
  };

  const alertContents = [
    {
      message:
        '하루가 지나면 대출조건이 달라질 수 있어요\n 그래도 나가시겠어요?',
      confirmText: '예',
      handleClickConfirm: () => closeCreditLoan(),
      cancelText: '아니요',
      handleClickCancel: () => setShowAlert(false),
    },
    {
      message: '오늘은 신청할 수 있는 상품이 없어요.\n내일 다시 확인해 주세요.',
      confirmText: '확인',
      handleClickConfirm: () => closeCreditLoan(),
    },
  ];

  const navigateCurrentProductItem = () => {
    beacon.sendIssueCatch({
      events: { subEventName: 'detail' },
      properties: {
        type: 'currentProductItem',
        requestSeq: todayLoanInfo?.currentProductItem?.requestSeq ?? 0,
        preRequestDtlNo: todayLoanInfo?.currentProductItem?.preRequestDtlNo,
      },
    });
    navigate(
      `/detail/${todayLoanInfo?.currentProductItem?.requestSeq ?? 0}/${
        todayLoanInfo?.currentProductItem?.preRequestDtlNo
      }`,
    );
  };

  const navigateResult = () => {
    navigate(`/result`, { state: { before: 'entry' } });
  };

  const condition = { custSeq: personal.custSeq };
  const setBaseCustProductInfoDay = useBaseParam(
    fetchCustTotalProductSummary,
    condition,
  );

  const getCustProductInfoDay = async () => {
    const res: any = await setBaseCustProductInfoDay();
    const temp = res?.data?.result;
    if (!temp) {
      setTodayLoanInfo(() => {
        return {};
      });
      return;
    }
    setTodayLoanInfo(() => {
      return {
        maxAmount: temp?.maxAmount,
        maxAmountText: temp?.maxAmountText,
        minInterest: temp?.minInterest,
        toDayRemainTime: temp?.toDayRemainTime,
        preRequestProcStatus: temp?.preRequestProcStatus,
        currentProductItem: temp?.currentProductItem && {
          key: temp?.currentProductItem?.custPreProductInfo?.preRequestDtlNo,
          preRequestDtlNo:
            temp?.currentProductItem?.custPreProductInfo?.preRequestDtlNo,
          logoImgUrl: temp?.currentProductItem?.productInfo?.logoImgUrl,
          companyNm: temp?.currentProductItem?.productInfo?.compNm,
          productNm: temp?.currentProductItem?.productInfo?.productNm,
          state: 'enabled',
        },
        preRequestHistInfoList: temp?.preRequestHistInfoList?.map(
          (item: any, idx: number) => {
            height.push({
              rate: item.interestRate ?? 0,
              amount: item.amount ?? 0,
            });
            return {
              state: idx === 3 ? 'recent' : 'past',
              rate: item.interestRate === 0 ? '?%' : item.interestRate + '%',
              amount: item.amount === 0 ? '?만 원' : currencyKR(item.amount),
              date: moment(item.histDate, 'YYYYMM').format('YYYY년 M월'),
            };
          },
        ),
        preRequestInfoData: temp?.preRequestInfoData,
      };
    });
  };

  useEffect(() => {
    if (todayLoanInfo?.preRequestProcStatus === '20') {
      setPersonal((prev: any) => {
        return {
          ...prev,
          transactionNo: todayLoanInfo?.preRequestInfoData?.transactionNo,
        };
      });
      const loanInfo = {
        regDate: todayLoanInfo?.preRequestInfoData?.regDate,
      };
      navigate('/loading', { state: loanInfo });
    }
  }, [todayLoanInfo?.preRequestProcStatus]);

  // banner
  const {
    isLoading: isLoadingBannerList,
    bannerList,
    hasBannerList,
  } = useBanner(
    media || '',
    PLACEMENT_TYPE.loan_same_day_reentry,
    baseContents.serverTime,
  );

  return {
    isRedirect,
    sendBeaconClkBtnDetailList,
    sendBeaconClkBtnRateRecord,
    sendBeaconClkBtnLimitRecord,
    sendBeaconClkBtnNotiSchAlrt,
    sendBeaconClkBtnIntrstCalc,
    sendBeaconClkBtnLoanPrdRecentHit,
    sendBeaconClkBtnEbmpOutlink,
    sendBeaconClkBnrBnrPromotion,
    todayLoanInfo,
    navigateBannerToPage,
    media,
    sortingItems,
    sort,
    closeWebview,
    setShowAlert,
    alertContent: alertContents[1],
    showAlert,
    onClickNaviButton,
    navigateNativeMain,
    navigateDA,
    navigateCurrentProductItem,
    navigateResult,
    changeSort,
    height,
    toDayRemainTime: todayLoanInfo?.toDayRemainTime?.split(':'),
    hasCurrentProductItem:
      todayLoanInfo?.currentProductItem &&
      Object.keys(todayLoanInfo?.currentProductItem).length > 0,
    hasTodayLoanInfo:
      todayLoanInfo?.minInterest !== 0 && todayLoanInfo?.maxAmount !== 0,
    isLoading: todayLoanInfo === undefined,
    isBridgeModal,
    setIsBridgeModal,

    bannerList: bannerList.map((item: any) => ({
      ...item,
      // todo send beacon - 기획 정의 x
      beacon: {
        click: () =>
          item.bannerSeq === 0
            ? sendBeaconClkBnrBnrPromotion('프로모션 배너')
            : () => {},
        view: () => {},
      },
    })),
    hasBannerList,
  };
};
export default useResultEntry;
